import React, {FC, useEffect, useRef, useState, useMemo} from 'react'
import {Dropdown, OverlayTrigger, Tooltip, Spinner} from 'react-bootstrap'
import EmojiPicker from 'emoji-picker-react'
import {useDispatch, useSelector} from 'react-redux'
import {ThunkDispatch} from 'redux-thunk'
import {AnyAction} from 'redux'
import {last, uniq} from 'lodash'
import moment from 'moment'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import styles from './MessageInputV3.module.scss'
import {
  getAllTemplateWithoutPagination,
  getTemplateListWithoutPagination,
} from '../../../MyTemplate/CreateScratch/CreateScratchSlices/CreateScratchSlices'
import {GetAllData} from '../../../reports-analytics/ShortLinkSlice/ShortLinkSlice'
import {getDedicatedNumber} from '../../../accounts/DedicatedNumber/DedicatedNumberSlice/DedicatedNumberSlice'
import {getCustomNunberList} from '../../../accounts/CustomNumber/CustomNumberSlice/CustomNumberSlice'
import {getBusinessName} from '../../../accounts/BusinessName/BusinessNameSlice/BusinessSlice'
import {
  getAllUserDetails,
  getDefaultShortUrl,
} from '../../../accounts/Overview/OverviewSlice/UserDetailsSlice'
import {confirm} from 'react-confirm-box'
import {AuthenticateUser} from '../../../../../Utils/_helpers/RoleAndPermission'
import {replaceMessageWith} from '../../../../../Utils/_helpers'
import {toast} from 'react-toastify'
import ContentEditableInputV3, {ContentEditableInputV3Ref} from './ContentEditableInputV3'
import ShortUrlModal from '../../../MyTemplate/CreateScratch/ShortUrlModal'
import {InputMessageContentRef} from '../../../../../Components/types/InputMessageContent'
import UnicodeModal from '../../../../../Components/UnicodeModal'
import {useTimezone} from '../../../../../hooks/useTimezone'

// Sender option with type information
interface SenderOption {
  value: string
  label: string
  type: 'shared' | 'dedicated' | 'custom' | 'business'
}

interface MessageInputV3Props {
  receiver: string
  onSend: (message: string, sender: string, senderType: string, longUrls?: any[]) => void
  onSendSchedule?: (message: string, sender: string, senderType: string, scheduleAt: Date, longUrls?: any[]) => void
  isSending?: boolean
  disabled?: boolean
  placeholder?: string
  // AI Suggestions props
  welcomeMessage?: string
  showAiSuggestion?: boolean
  onToggleAiSuggestion?: () => void
  onUseAiSuggestion?: () => void
  isBcardUser?: boolean
}

// Helper function to calculate message count and type
const calculateMessageStats = (message: string) => {
  // GSM 7-bit character set - includes newline (\n) and carriage return (\r)
  const GSM_7BIT_CHARS =
    '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà^{}\\[~]|€'

  const isGSM = (char: string) => GSM_7BIT_CHARS.includes(char)
  const isUnicode = message.split('').some((char) => !isGSM(char))

  if (isUnicode) {
    const maxPerMessage = message.length <= 70 ? 70 : 67
    const parts = message.length === 0 ? 0 : Math.ceil(message.length / maxPerMessage)
    return {
      type: 'Unicode',
      length: message.length,
      remaining:
        parts === 0 ? 70 : maxPerMessage - (message.length % maxPerMessage || maxPerMessage),
      maxdata: parts === 0 ? 70 : maxPerMessage,
      credit: parts || 1,
    }
  } else {
    const maxPerMessage = message.length <= 160 ? 160 : 153
    const parts = message.length === 0 ? 0 : Math.ceil(message.length / maxPerMessage)
    return {
      type: 'GSM',
      length: message.length,
      remaining:
        parts === 0 ? 160 : maxPerMessage - (message.length % maxPerMessage || maxPerMessage),
      maxdata: parts === 0 ? 160 : maxPerMessage,
      credit: parts || 1,
    }
  }
}

/**
 * Get the default sender based on user type and available numbers
 * Priority: Dedicated Number (with isDefault) > Custom Number (with isDefault) > Business Name (with isDefault) > Shared Number
 */
const getDefaultSenderValue = (
  dedicatedNumbers: any[],
  customNumbers: any[],
  businessNames: any[],
  userDetail: any
): string => {
  const isSubClient =
    userDetail?.subClientAccountId && userDetail._id !== userDetail.subClientAccountId

  // Check dedicated numbers first
  if (dedicatedNumbers?.length > 0) {
    if (isSubClient) {
      const defaultNum = dedicatedNumbers.find(
        (key: any) => key?.subClientDefaults && key.subClientDefaults[userDetail._id] === true
      )
      if (defaultNum?.audNumber) return defaultNum.audNumber
    } else {
      const defaultNum = dedicatedNumbers.find((key: any) => key?.isDefault)
      if (defaultNum?.audNumber) return defaultNum.audNumber
    }
  }

  // Check custom numbers
  if (customNumbers?.length > 0) {
    const defaultCustom = customNumbers.find((key: any) => key?.isDefault)
    if (defaultCustom?.number) return defaultCustom.number
  }

  // Check business names
  if (businessNames?.length > 0) {
    const defaultBusiness = businessNames.find((key: any) => key?.isDefault)
    if (defaultBusiness?.businessname) return defaultBusiness.businessname
  }

  return '#SharedNum#'
}

/**
 * Get sender type based on the sender value and available options
 */
const getSenderTypeFromValue = (senderValue: string, senderOptions: SenderOption[]): string => {
  const option = senderOptions.find((opt) => opt.value === senderValue)
  if (!option) return '#SharedNum#'

  switch (option.type) {
    case 'dedicated':
      return 'dedicatedNumber'
    case 'custom':
      return 'customNumber'
    case 'business':
      return 'businessNumber'
    case 'shared':
    default:
      return '#SharedNum#'
  }
}

const MessageInputV3: FC<MessageInputV3Props> = ({
  receiver,
  onSend,
  onSendSchedule,
  isSending = false,
  disabled = false,
  placeholder = 'Type a message...',
  welcomeMessage,
  showAiSuggestion = false,
  onToggleAiSuggestion,
  onUseAiSuggestion,
  isBcardUser = false,
}) => {
  const dispatch = useDispatch()
  const thunkDispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>()

  const contentEditableRef = useRef<ContentEditableInputV3Ref>(null)
  const emojiPickerRef = useRef<HTMLDivElement>(null)

  const [message, setMessage] = useState('')
  const [longUrls, setLongUrls] = useState<any[]>([])
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [senderNumber, setSenderNumber] = useState<string>('#SharedNum#')
  const [showShortUrlModal, setShowShortUrlModal] = useState(false)
  const [sendLater, setSendLater] = useState(false)
  const [laterDate, setLaterDate] = useState<Date>(new Date())

  const {getUserTimezoneNow} = useTimezone()

  useEffect(() => {
    setLaterDate(getUserTimezoneNow())
  }, [getUserTimezoneNow])

  // Mock ref adapter for ShortUrlModal - it expects InputMessageContentRef but we use ContentEditableInputV3Ref
  const mockInputRef = useRef<InputMessageContentRef>({
    selectionStart: 0,
    selectionEnd: 0,
    focus: () => contentEditableRef.current?.focus(),
    insertPlaceholder: (placeholder: string) =>
      contentEditableRef.current?.insertPlaceholder(placeholder),
    getRawText: () => message,
  })

  // Selectors
  const templates: any = useSelector(getTemplateListWithoutPagination)
  const ShortLinkList = useSelector((state: any) => state?.ShortLink?.allTrackingLinks)
  const CustomFieldName: any = useSelector(
    (state: any) => state?.subscriberData?.FieldName?.data?.fields
  )
  const defaultDomain = useSelector(getDefaultShortUrl)
  const userDetail = useSelector(getAllUserDetails)
  // Sender number sources
  const dedicatedNumbers = useSelector(getDedicatedNumber)
  const customNumbers = useSelector(getCustomNunberList)
  const businessNames = useSelector(getBusinessName)

  // Build sender options from all sources
  const senderOptions = useMemo<SenderOption[]>(() => {
    const options: SenderOption[] = [{value: '#SharedNum#', label: 'Shared Number', type: 'shared'}]

    // Add all dedicated numbers (active status only)
    if (dedicatedNumbers?.length > 0) {
      dedicatedNumbers
        .filter((num: any) => num?.status?.toLowerCase() === 'active')
        .forEach((num: any) => {
          options.push({
            value: num.audNumber,
            label: `DN: ${num.audNumber}`,
            type: 'dedicated',
          })
        })
    }

    // Add custom numbers (active status only)
    if (customNumbers?.length > 0) {
      customNumbers
        .filter((num: any) => num?.status === 'active')
        .forEach((num: any) => {
          if (num?.number) {
            options.push({
              value: num.number,
              label: `Custom: ${num.number}`,
              type: 'custom',
            })
          }
        })
    }

    // Add business names (ACTIVE status only)
    if (businessNames?.length > 0) {
      businessNames
        .filter((bn: any) => bn?.status === 'ACTIVE')
        .forEach((bn: any) => {
          if (bn?.businessname) {
            options.push({
              value: bn.businessname,
              label: `Business: ${bn.businessname}`,
              type: 'business',
            })
          }
        })
    }

    return options
  }, [dedicatedNumbers, customNumbers, businessNames])

  // Initialize data
  useEffect(() => {
    thunkDispatch(GetAllData({}))
    AuthenticateUser('templates-list') && dispatch(getAllTemplateWithoutPagination())
  }, [])

  // Auto-select default sender when sender sources or userDetail changes
  useEffect(() => {
    if (userDetail) {
      const defaultSender = getDefaultSenderValue(
        dedicatedNumbers,
        customNumbers,
        businessNames,
        userDetail
      )
      setSenderNumber(defaultSender)
    }
  }, [dedicatedNumbers, customNumbers, businessNames, userDetail])

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
        setShowEmojiPicker(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Custom fields options
  const customFieldOptions =
    CustomFieldName?.length > 0
      ? uniq(CustomFieldName as string[]).map((key: string) => ({
          value: `{{[${key}]}}`,
          label: key,
        }))
      : []

  // Templates options (SMS only)
  const templateOptions =
    templates?.length > 0
      ? templates
          ?.filter(
            (key: any) =>
              key?.type?.toUpperCase() === 'SMS' &&
              (key?.category?.toUpperCase() === 'GENERAL' || !key?.category)
          )
          ?.map((key: any) => ({
            value: key?.name,
            label: key?.name,
          }))
      : []

  // Short link options
  const shortLinkOptions =
    ShortLinkList?.map((key: any) => ({
      shortUrl: key?.shortUrl,
      upstreamUrl: key?.upstreamUrl,
      label: `${last(key?.shortUrl?.split('/'))} (${key?.upstreamUrl})`,
    })) || []

  // BulkMessageData adapter for ShortUrlModal
  // The modal expects { message: string, longUrl: any[] } format
  const bulkMessageData = useMemo(
    () => ({
      message: message,
      longUrl: longUrls,
    }),
    [message, longUrls]
  )

  // Callback to update message and longUrls from ShortUrlModal
  const setBulkMessageData = (data: any) => {
    if (data.message !== undefined) {
      setMessage(data.message)
    }
    if (data.longUrl !== undefined) {
      setLongUrls(data.longUrl)
    }
  }

  // Calculate message stats
  const messageStats = calculateMessageStats(message)

  // Unicode modal state
  const [showUnicodeModal, setShowUnicodeModal] = useState(false)

  // Handlers
  const handleMessageChange = (value: string) => {
    setMessage(value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleSend = () => {
    if (!message.trim() || isSending || disabled) return
    const senderType = getSenderTypeFromValue(senderNumber, senderOptions)

    if (sendLater) {
      if (new Date(laterDate) <= getUserTimezoneNow()) {
        toast.error('Scheduled time must be in the future')
        return
      }
      if (onSendSchedule) {
        onSendSchedule(message.trim(), senderNumber, senderType, laterDate, longUrls)
      }
    } else {
      onSend(message.trim(), senderNumber, senderType, longUrls)
    }

    setMessage('')
    setLongUrls([])
    setSendLater(false)
    setLaterDate(getUserTimezoneNow())
    contentEditableRef.current?.clear()
  }

  const handleEmojiClick = (emojiData: any) => {
    contentEditableRef.current?.insertEmoji(emojiData.emoji)
    setShowEmojiPicker(false)
  }

  const handleCustomFieldSelect = (option: any) => {
    if (!option?.value) return
    // Extract placeholder name from {{[FieldName]}} format
    const placeholderMatch = option.value.match(/\{\{\[(.+?)\]\}\}/)
    if (placeholderMatch) {
      contentEditableRef.current?.insertPlaceholder(placeholderMatch[1])
    }
  }

  const handleShortUrlSelect = (option: any) => {
    if (option?.value === 'create') {
      // Open the ShortUrlModal to create a new short link
      setShowShortUrlModal(true)
      return
    }

    if (option?.shortUrl && option?.upstreamUrl) {
      const urlText = `${defaultDomain}/${option.shortUrl}`
      // Update message state with the URL - this triggers updateContentEditable
      // which will parse the URL and convert it to a badge
      const newMessage = message ? `${message} ${urlText} ` : `${urlText} `
      setMessage(newMessage)
      setLongUrls([...longUrls, {longUrl: option.upstreamUrl, shortUrl: option.shortUrl}])
      contentEditableRef.current?.focus()
    }
  }

  const handleTemplateSelect = async (option: any) => {
    if (!option?.value) return

    const confirmation = await confirm(
      `Are you sure you want to use the "${option.value}" template?`,
      {closeOnOverlayClick: false, labels: {confirmable: 'Yes', cancellable: 'No'}}
    )

    if (confirmation) {
      const templateData = templates?.find((t: any) => t?.name === option?.value)
      if (templateData) {
        const templateMessage = replaceMessageWith(templateData?.message, '')
        setMessage(templateMessage || '')

        // Handle template long URLs
        if (templateData?.longUrl && Array.isArray(templateData.longUrl)) {
          const filteredUrls = templateData.longUrl.filter((item: any) => typeof item !== 'string')
          setLongUrls(filteredUrls)
        }

        contentEditableRef.current?.focus()
      }
    }
  }

  const renderTooltip = (text: string) => <Tooltip id={`tooltip-${text}`}>{text}</Tooltip>

  // Handle using the AI suggestion
  const handleUseAiSuggestion = () => {
    if (welcomeMessage) {
      setMessage(welcomeMessage)
      contentEditableRef.current?.focus()
      // Notify parent to close the suggestion bubble
      if (onUseAiSuggestion) {
        onUseAiSuggestion()
      }
    }
  }

  return (
    <div className={styles.messageInputContainer}>
      {/* AI Suggestion Bubble - appears above the message input */}
      {welcomeMessage && showAiSuggestion && (
        <div className={styles.aiSuggestionBubble}>
          <div className={styles.aiSuggestionHeader}>
            <i className='bi bi-chat-quote-fill me-2'></i>
            <span>Suggested welcome message</span>
            <button
              type='button'
              className={styles.aiSuggestionClose}
              onClick={onToggleAiSuggestion}
              aria-label='Close'
            >
              <i className='bi bi-x-lg'></i>
            </button>
          </div>
          <div className={styles.aiSuggestionContent}>{welcomeMessage}</div>
          <div className={styles.aiSuggestionActions}>
            <button
              type='button'
              className={styles.aiSuggestionUseBtn}
              onClick={handleUseAiSuggestion}
            >
              Use
            </button>
          </div>
        </div>
      )}

      {/* Top Bar - Sender Selection and AI Suggestions */}
      <div className={styles.topBar}>
        {/* Left: From Label + Sender Number Dropdown */}
        <div className={styles.senderSection}>
          <span className={styles.fromLabel}>From:</span>
          <Dropdown className={styles.senderDropdown}>
            <Dropdown.Toggle as='button' className={styles.senderButton} disabled={disabled}>
              <i className='bi bi-phone me-2'></i>
              <span>
                {senderOptions.find((opt) => opt.value === senderNumber)?.label || 'Select Sender'}
              </span>
              <i className='bi bi-chevron-down ms-2'></i>
            </Dropdown.Toggle>
            <Dropdown.Menu className={styles.senderDropdownMenu}>
              {senderOptions.map((opt) => (
                <Dropdown.Item
                  key={opt.value}
                  active={senderNumber === opt.value}
                  onClick={() => setSenderNumber(opt.value)}
                >
                  {opt.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {/* Right: Character Count */}
        <div className={styles.topBarRight}>
          {/* AI Suggestions Button - Hidden until API is ready
          <button
            type='button'
            className={styles.aiButton}
            onClick={() => toast.info('AI Suggestions coming soon!')}
            disabled={disabled}
          >
            <i className='bi bi-stars me-2'></i>
            AI Suggestions
          </button>
          */}

          {/* Character Count */}
          <div className={styles.charCount}>
            <span className={styles.remaining}>{messageStats.remaining} chars</span>
            <span className={styles.separator}>•</span>
            <span
              className={styles.smsParts}
              style={{
                cursor: messageStats.type === 'Unicode' ? 'pointer' : 'default',
                border: messageStats.type === 'Unicode' ? '2px solid #ff0000' : 'none',
                padding: messageStats.type === 'Unicode' ? '2px 4px' : undefined,
                borderRadius: messageStats.type === 'Unicode' ? '4px' : undefined,
              }}
              onClick={messageStats.type === 'Unicode' ? () => setShowUnicodeModal(true) : undefined}
            >
              {messageStats.type} ({messageStats.credit} SMS Part
              {messageStats.credit !== 1 ? 's' : ''})
            </span>
            {messageStats.type === 'Unicode' && (
              <i
                className='fas fa-exclamation-circle text-warning ms-2'
                style={{fontSize: '14px', cursor: 'pointer'}}
                onClick={() => setShowUnicodeModal(true)}
              ></i>
            )}
          </div>
        </div>
      </div>

      {/* Text Area */}
      <div className={styles.textAreaWrapper}>
        <ContentEditableInputV3
          ref={contentEditableRef}
          className={styles.textArea}
          value={message}
          onChange={handleMessageChange}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? 'Select a conversation' : placeholder}
          disabled={disabled || isSending}
        />
      </div>

      {/* Bottom Action Bar */}
      <div className={styles.actionBar}>
        {/* Left Side - Action Icons */}
        <div className={styles.actionIcons}>
          {/* Emoji Picker */}
          <div className={styles.emojiWrapper} ref={emojiPickerRef}>
            <OverlayTrigger placement='top' overlay={renderTooltip('Emoji')}>
              <button
                type='button'
                className={styles.actionIcon}
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                disabled={disabled}
              >
                <i className='bi bi-emoji-smile'></i>
              </button>
            </OverlayTrigger>
            {showEmojiPicker && (
              <div className={styles.emojiPickerPopup}>
                <EmojiPicker onEmojiClick={handleEmojiClick} width={300} height={350} />
              </div>
            )}
          </div>

          {/* Custom Field Dropdown */}
          {customFieldOptions.length > 0 && (
            <Dropdown>
              <OverlayTrigger placement='top' overlay={renderTooltip('Custom Field')}>
                <Dropdown.Toggle as='button' className={styles.actionIcon} disabled={disabled}>
                  <i className='bi bi-braces'></i>
                </Dropdown.Toggle>
              </OverlayTrigger>
              <Dropdown.Menu className={styles.actionDropdownMenu}>
                {customFieldOptions.map((opt: any) => (
                  <Dropdown.Item key={opt.value} onClick={() => handleCustomFieldSelect(opt)}>
                    {opt.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}

          {/* Template Dropdown */}
          {templateOptions.length > 0 && (
            <Dropdown>
              <OverlayTrigger placement='top' overlay={renderTooltip('Templates')}>
                <Dropdown.Toggle as='button' className={styles.actionIcon} disabled={disabled}>
                  <i className='bi bi-file-text'></i>
                </Dropdown.Toggle>
              </OverlayTrigger>
              <Dropdown.Menu className={styles.actionDropdownMenu}>
                {templateOptions.map((opt: any) => (
                  <Dropdown.Item key={opt.value} onClick={() => handleTemplateSelect(opt)}>
                    {opt.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}

          {/* Short URL Dropdown */}
          <div className={styles.dropdownWrapper}>
            <Dropdown>
              <OverlayTrigger placement='top' overlay={renderTooltip('Short URL')}>
                <Dropdown.Toggle as='button' className={styles.actionIcon} disabled={disabled}>
                  <i className='bi bi-link-45deg'></i>
                </Dropdown.Toggle>
              </OverlayTrigger>
              <Dropdown.Menu className={styles.actionDropdownMenu}>
                <Dropdown.Item onClick={() => handleShortUrlSelect({value: 'create'})}>
                  <i className='bi bi-plus-circle me-2'></i>Create New
                </Dropdown.Item>
                {shortLinkOptions.length > 0 && <Dropdown.Divider />}
                {shortLinkOptions.map((opt: any, idx: number) => (
                  <Dropdown.Item
                    key={idx}
                    className={styles.shortUrlItem}
                    onClick={() => handleShortUrlSelect(opt)}
                  >
                    <span className={styles.shortUrlLabel}>{opt.label}</span>
                    {opt.upstreamUrl && (
                      <button
                        className={styles.shortUrlPreviewBtn}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation()
                          window.open(opt.upstreamUrl, '_blank', 'noopener,noreferrer')
                        }}
                        title='Preview URL'
                      >
                        <i className='bi bi-box-arrow-up-right'></i>
                      </button>
                    )}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* AI Suggestions Button - Only visible for bcard users */}
          {isBcardUser && (
            <OverlayTrigger placement='top' overlay={renderTooltip('AI Suggestions')}>
              <button
                type='button'
                className={`${styles.actionIcon} ${styles.aiActionIcon} ${
                  showAiSuggestion ? styles.aiActive : ''
                }`}
                onClick={() => {
                  if (welcomeMessage && onToggleAiSuggestion) {
                    onToggleAiSuggestion()
                  } else {
                    toast.info('No AI suggestion available for this contact')
                  }
                }}
                disabled={disabled}
              >
                <i className='bi bi-stars'></i>
              </button>
            </OverlayTrigger>
          )}
        </div>

        {/* Right Side - Send Button */}
        <div className='d-flex align-items-center gap-2'>
          <div className='form-check mb-0'>
            <input
              className='form-check-input'
              type='checkbox'
              id='sendLaterCheck'
              checked={sendLater}
              onChange={() => setSendLater(!sendLater)}
            />
            <label className='form-check-label' htmlFor='sendLaterCheck' style={{fontSize: '12px', whiteSpace: 'nowrap'}}>
              Send Later
            </label>
          </div>
          {sendLater && (
            <div className='d-flex align-items-center gap-2'>
              <DatePicker
                minDate={getUserTimezoneNow()}
                dateFormat='MMM d, yyyy h:mm aa'
                placeholderText='Select date & time'
                showTimeSelect
                className='form-control form-control-sm'
                onChange={(date: Date) => {
                  if (new Date(date) > getUserTimezoneNow()) {
                    setLaterDate(date)
                  } else {
                    toast.error('Please select a future date and time')
                  }
                }}
                selected={laterDate}
              />
              {laterDate && new Date(laterDate) > getUserTimezoneNow() && (
                <small className='text-muted' style={{fontSize: '11px', whiteSpace: 'nowrap'}}>
                  {moment(laterDate).format('MMM Do, h:mm A')}
                </small>
              )}
            </div>
          )}
          <button
            type='button'
            className='btn btn-primary btn-sm p-3 fw-500 px-5 d-flex align-items-center gap-2'
            onClick={handleSend}
            disabled={
              !message.trim() ||
              isSending ||
              disabled ||
              (sendLater && new Date(laterDate) <= getUserTimezoneNow())
            }
          >
            {isSending ? (
              <>
                <Spinner animation='border' size='sm' />
                <span>{sendLater ? 'Scheduling...' : 'Sending...'}</span>
              </>
            ) : (
              <>
                <span>{sendLater ? 'Schedule' : 'Send'}</span>
                <i className='bi bi-send-fill'></i>
              </>
            )}
          </button>
        </div>
      </div>

      <UnicodeModal
        show={showUnicodeModal}
        onHide={() => setShowUnicodeModal(false)}
        message={message}
      />

      {/* Short URL Creation Modal */}
      <ShortUrlModal
        inputRef={mockInputRef}
        setBulkMessageData={setBulkMessageData}
        bulkMessageData={bulkMessageData}
        isShow={showShortUrlModal}
        onClose={() => setShowShortUrlModal(false)}
      />
    </div>
  )
}

export default MessageInputV3